import { InfoIcon, Loader2Icon, PencilIcon } from 'lucide-react'
import { useState } from 'react'

import {
  GetCompanyDrawerMetadataResponse,
  IRQCategorySeverity,
} from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { useHasPermissions } from '@/lib/app/check-permissions.hook'
import { useInherentRiskItems } from '@/lib/risk/inherent-risk-items.hook'

import { InherentRiskCategoriesSummary } from '@/pages/company-drawer/company-overview/inherent-risk-categories-summary/inherent-risk-categories-summary'
import { InherentRiskChart } from '@/pages/company-drawer/company-overview/inherent-risk-chart/inherent-risk-chart'
import { INHERENT_RISK_PROFILE_LABEL } from '@/pages/company-drawer/company-overview/inherent-risk-profile/labels'
import { SetIrqModal } from '@/pages/company-drawer/company-overview/set-irq-modal/set-irq-modal'
import { UserLastEngagementIrqUpdateInfo } from '@/pages/company-drawer/company-overview/user-last-engagement-irq-update-info/user-last-engagement-irq-update-info'
import { UserScoreInfo } from '@/pages/company-drawer/company-overview/user-score-info/user-score-info'

import { Alert } from '@/components/lemmala-design-system/alert/alert'
import { Button } from '@/components/lemmala-design-system/button/button'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'
import { isEstimatedRisk } from '@/components/shared/risk-badge/utils'

type InherentRiskProfileProps = {
  companyRisk: RiskLevel
  irqCategorySeverities: IRQCategorySeverity[]
  isIrqTemplateOutdated: GetCompanyDrawerMetadataResponse['isIrqTemplateOutdated']
  showCta?: boolean
  calculatedRisk?: RiskLevel
  validatedRisk?: RiskLevel
  companyId: string
  engagementId?: string
  engagementName?: string
  engagementModeEnabled: boolean
}

export const InherentRiskProfile = ({
  companyRisk,
  irqCategorySeverities,
  showCta = true,
  calculatedRisk,
  validatedRisk,
  companyId,
  engagementId,
  engagementName,
  engagementModeEnabled,
  isIrqTemplateOutdated,
}: InherentRiskProfileProps) => {
  const [isEngagementIrqOpen, setIsEngagementIrqOpen] = useState(false)
  const [isCompanyIrqOpen, setIsCompanyIrqOpen] = useState(false)

  const inherentRiskData = useInherentRiskItems(companyId, engagementId)

  const hasBaseManagePermissions = useHasPermissions(['base.manage'])
  const hasIrqManagePermissions = useHasPermissions(['irq.manage'])

  const hasSteps = !engagementModeEnabled || hasBaseManagePermissions

  const isCtaVisible = showCta && hasSteps && !(isIrqTemplateOutdated && !engagementModeEnabled)
  const isValidateCta = isEstimatedRisk(validatedRisk) && hasIrqManagePermissions

  const textElement = hasIrqManagePermissions ? (
    <TextWithIcon
      icon={<PencilIcon className='size-3.5' />}
      text={INHERENT_RISK_PROFILE_LABEL.EDIT}
    />
  ) : (
    INHERENT_RISK_PROFILE_LABEL.VIEW_DETAILS
  )

  const openModal = engagementId
    ? () => setIsEngagementIrqOpen(true)
    : () => setIsCompanyIrqOpen(true)

  return (
    <div className='p-6'>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <div className='whitespace-nowrap text-heading-small'>
          {INHERENT_RISK_PROFILE_LABEL.HEADING}
        </div>
        {isCtaVisible &&
          (isValidateCta ? (
            <Button onClick={openModal}>
              {INHERENT_RISK_PROFILE_LABEL.VALIDATE_INHERENT_RISK}
            </Button>
          ) : (
            <Button variant='ghost' size='sm' onClick={openModal}>
              {textElement}
            </Button>
          ))}
      </div>
      <div>
        {engagementId ? (
          <UserLastEngagementIrqUpdateInfo companyId={companyId} engagementId={engagementId} />
        ) : (
          <UserScoreInfo companyId={companyId} />
        )}
      </div>
      {isIrqTemplateOutdated && !engagementModeEnabled && (
        <Alert
          icon={<InfoIcon className='size-4' />}
          layout='vertical'
          className='mt-4 items-center'
          description={
            <div className='flex min-w-0 items-center justify-between gap-2'>
              <div
                title={INHERENT_RISK_PROFILE_LABEL.OUTDATED_QUESTIONNAIRE_ALERT}
                className='truncate text-body-default'
              >
                {INHERENT_RISK_PROFILE_LABEL.OUTDATED_QUESTIONNAIRE_ALERT}
              </div>
              <Button
                className={'shrink-0 !text-main-foreground-accent underline !text-label-default'}
                variant='link'
                onClick={openModal}
              >
                {textElement}
              </Button>
            </div>
          }
        />
      )}
      <div className='flex justify-between'>
        <div>
          <div className='my-4'>
            {calculatedRisk !== RiskLevel.UNSPECIFIED ? (
              <Tooltip
                trigger={
                  <RiskBadge
                    riskLevel={companyRisk}
                    className='gap-2 px-4 py-3'
                    variant={isEstimatedRisk(validatedRisk) ? 'estimated' : 'highlighted'}
                  />
                }
                tooltipDisabled={!isEstimatedRisk(validatedRisk)}
                size='large'
              >
                {INHERENT_RISK_PROFILE_LABEL.ESTIMATED_BADGE_TOOLTIP}
                {isCtaVisible && isValidateCta && (
                  <>
                    <br />
                    {INHERENT_RISK_PROFILE_LABEL.ESTIMATED_BADGE_TOOLTIP_CTA_PREFIX}
                    <br />
                    {INHERENT_RISK_PROFILE_LABEL.ESTIMATED_BADGE_TOOLTIP_CTA_SUFFIX}
                  </>
                )}
              </Tooltip>
            ) : (
              <Alert
                variant={'info'}
                icon={<Loader2Icon className='mr-0 size-4 animate-spin duration-2000' />}
                description={INHERENT_RISK_PROFILE_LABEL.ESTIMATING_INHERENT_RISK}
              />
            )}
          </div>

          <InherentRiskCategoriesSummary irqCategorySeverities={irqCategorySeverities} />
        </div>
        <div className='max-w-112'>
          <InherentRiskChart
            companyRisk={companyRisk}
            irqCategorySeverities={irqCategorySeverities}
          />
        </div>
      </div>
      {isEngagementIrqOpen && (
        <SetIrqModal
          companyId={companyId}
          mode='edit'
          from='inherent-risk-profile'
          engagementModeEnabled={engagementModeEnabled}
          engagementId={engagementId}
          engagementName={engagementName}
          inherentRiskData={inherentRiskData}
          isIrqTemplateOutdated={isIrqTemplateOutdated}
          onClose={() => setIsEngagementIrqOpen(false)}
        />
      )}
      {isCompanyIrqOpen && (
        <SetIrqModal
          companyId={companyId}
          mode='edit'
          from='inherent-risk-profile'
          engagementModeEnabled={engagementModeEnabled}
          inherentRiskData={inherentRiskData}
          isIrqTemplateOutdated={isIrqTemplateOutdated}
          onClose={() => setIsCompanyIrqOpen(false)}
        />
      )}
    </div>
  )
}
